import {
  faFileAlt,
  faPencilAlt,
  faTrashAlt,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { B2Button } from "@hdb/biome-2";
import { FC, createRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import ModalComponent from "../../../../../core/components/modal/modal.component";
import TableComponent from "../../../../../core/components/table/table.component";
import HasPermission from "../../../../../core/guards/has-permission";
import SkeletonComponent from "../../../../../shared/custom-spinner/skeleton.component";
import FormBuilderComponent, {
  FormConfig,
} from "../../../../../shared/form-builder/form-builder.component";
import { dataUrlToFile } from "../../../../../shared/helpers/data-url-to-file";
import {
  addAssetModel,
  deleteAssetModel,
  downloadAssetModelFile,
  editAssetModel,
  getAssetModels,
  getAssetModelsByCode,
} from "../../../../../store/slices/asset-model.slice";
import { getAssetTypes } from "../../../../../store/slices/asset-type";
import { getSLS } from "../../../../../store/slices/sls.slice";
import SearchBarComponent from "../../search-bar/search-bar.component";
import { Spinner } from "react-bootstrap";
import {
  validateImageFile,
  imageFileTypes,
} from "src/shared/helpers/validate-Files";
import "../../../admin.scss";

interface AssetModelTableComponentProps {
  columns: any;
  data: any[];
  totalNumberOfRecords: number;
  pageSize: number;
  current: number;
  onChangePage: any;
  onChangeSort: any;
  onRefetchData: () => void;
  isLoading: boolean;
  onSearch: (val: string) => void;
  assetModelSearchValue: string;
}

const AssetModelTableComponent: FC<AssetModelTableComponentProps> = ({
  columns,
  data,
  totalNumberOfRecords,
  pageSize,
  current,
  onChangePage,
  onChangeSort,
  onRefetchData,
  isLoading,
  onSearch,
  assetModelSearchValue,
}) => {
  const formRef = createRef();
  const [preview, setPreview] = useState<any>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isDetails, setIsDetails] = useState<boolean>(false);
  const [isShowImage, setIsShowImage] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [slsSupplierOptions, setSlsSupplierOptions] = useState<any>([]);
  const [assetTypeOptions, setAssetTypeOptions] = useState<any>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>();
  const [selectedIdToDeleteOrEdit, setSelectedIdToDeleteOrEdit] =
    useState<number>(0);
  // const [isPdfDownload, setIsPdfDownload] = useState<boolean>(false);
  const [isExcelDownload, setIsExcelDownload] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<any>({
    assetModelCode: "",
    ratedWatt: "",
    supplierId: "",
    expectedLifeTime: "",
    picture: "",
    assetTypeId: "",
    description: "",
    remark: "",
  });

  const dispatch = useDispatch();

  const assetModelDetails = useSelector((state: any) => {
    return state.assetModel.assetModelDetails;
  });

  const isLoadingAssetModelDownload = useSelector((state: any) => {
    return state.assetModel.isLoadingAssetModelDownload;
  });

  const isLoadingAssetModelDetails = useSelector((state: any) => {
    return state.assetModel.isLoadingAssetModelDetails;
  });

  const slsSupplierResponse = useSelector((state: any) => {
    return state.sls.sls;
  });

  const isLoadingSls = useSelector((state: any) => {
    return state.sls.isLoadingSls;
  });

  const assetTypeResponse = useSelector((state: any) => {
    return state.assetType.assetTypes;
  });

  useEffect(() => {
    const sls = slsSupplierResponse?.data?.map((sl: any) => ({
      label: sl?.name,
      value: sl?.id,
    }));

    const assetType = assetTypeResponse?.data?.map((item: any) => ({
      label: item?.name,
      value: item?.id,
    }));

    setSlsSupplierOptions(sls);
    setAssetTypeOptions(assetType);
  }, [slsSupplierResponse, assetTypeResponse]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEdit(false);
    setIsDetails(false);
    setIsDelete(false);
    setIsShowImage(false);
    setSelectedFile(null);

    setInitialValues({
      assetModelCode: "",
      ratedWatt: "",
      supplierId: "",
      expectedLifeTime: "",
      picture: "",
      assetTypeId: "",
      description: "",
      remark: "",
    });
  };

  const detailsAction = (assetModelCode: any) => {
    setIsDetails(true);
    setIsModalOpen(true);
    setIsEdit(false);
    setIsDelete(false);
    setIsShowImage(true);

    Promise.all([
      dispatch(getSLS({ PageNumber: 0, pageSize: 0 })),
      dispatch(getAssetTypes({ PageNumber: 0, pageSize: 0 })),
      dispatch(getAssetModelsByCode({ assetModelCode })),
    ])
      .then(() => { })
      .catch((error: any) => {
        console.log("Error in api call: ", error);
      });
  };

  const editAction = (assetModelCode: any) => {
    setIsDetails(false);
    setIsModalOpen(true);
    setIsEdit(true);
    setIsDelete(false);
    setIsShowImage(true);

    Promise.all([
      dispatch(getSLS({ PageNumber: 0, pageSize: 0 })),
      dispatch(getAssetTypes({ PageNumber: 0, pageSize: 0 })),
      dispatch(getAssetModelsByCode({ assetModelCode })),
    ])
      .then(() => { })
      .catch((error: any) => {
        console.log("Error in api call: ", error);
      });

    setSelectedIdToDeleteOrEdit(assetModelCode);
  };

  const deleteAction = (assetModelCode: any) => {
    setIsDetails(false);
    setIsModalOpen(true);
    setIsEdit(false);
    setIsDelete(true);

    setSelectedIdToDeleteOrEdit(assetModelCode);
  };

  const handleDelete = () => {
    dispatch(
      deleteAssetModel({ assetModelCode: selectedIdToDeleteOrEdit })
    ).then(() => {
      onRefetchData();
      handleCloseModal();
    });
  };

  const handleAddAssetModel = () => {
    Promise.all([
      dispatch(getSLS({ PageNumber: 0, pageSize: 0 })),
      dispatch(getAssetTypes({ PageNumber: 0, pageSize: 0 })),
    ])
      .then(() => {
        setIsDetails(false);
        setIsModalOpen(true);
        setIsEdit(false);
        setIsDelete(false);
        setIsShowImage(false);
      })
      .catch((error: any) => {
        console.log("Error in api call: ", error);
      });
  };

  useEffect(() => {
    if (!assetModelDetails || !Object.keys(assetModelDetails).length) return;
    // debugger;
    setInitialValues({
      assetModelCode: assetModelDetails?.assetModelCode || "",
      ratedWatt: assetModelDetails?.ratedWatt || "",
      supplierId: assetModelDetails?.supplierId || "",
      expectedLifeTime: assetModelDetails?.expectedLifeTime || "",
      picture: assetModelDetails?.picture || "",
      assetTypeId: assetModelDetails?.assetTypeId || "",
      description: assetModelDetails?.description || "",
      remark: assetModelDetails?.remark || "",
    });

    if (assetModelDetails?.picture === null) {
      setSelectedFile(null);
      setIsShowImage(false);
    } else if (assetModelDetails?.picture) {
      const dataUrl = assetModelDetails?.picture || "";
      const filename = "image.png";
      const file = dataUrlToFile(dataUrl, filename);

      setSelectedFile(file);
      setIsShowImage(true);
    }
  }, [assetModelDetails]);

  const onSubmit = () => {
    const form: any = formRef.current;

    if (form) {
      form.handleSubmit();

      const {
        assetModelCode,
        assetTypeId,
        expectedLifeTime,
        ratedWatt,
        supplierId,
        // picture,
        description,
        remark,
      } = form.values;

      if (
        !assetModelCode ||
        !assetTypeId ||
        !expectedLifeTime ||
        !ratedWatt ||
        !supplierId
      )
        return;

      if (
        assetModelCode?.length > 50 ||
        expectedLifeTime <= 0 ||
        expectedLifeTime > 1000000000 ||
        ratedWatt <= 0 ||
        ratedWatt > 1000 ||
        description?.length > 200 ||
        remark?.length > 200
      ) {
        return;
      }

      if (isEdit) {
        const formValues = form.values;
        const payload = {
          ...formValues,
          assetModelCode: selectedIdToDeleteOrEdit,
        };

        dispatch(
          editAssetModel({
            payload,
            assetModelCode: selectedIdToDeleteOrEdit,
            file: selectedFile || null,
          })
        ).then(() => {
          handleCloseModal();
          onRefetchData();
        });

        setSelectedFile(null);
      } else {
        document.body.classList.add("loading-indicator");

        dispatch(
          addAssetModel({ payload: form.values, file: selectedFile || null })
        ).then(() => {
          handleCloseModal();
          onRefetchData();
          setSelectedFile(null);
        }).finally(() => {
          document.body.classList.remove("loading-indicator");
        });
      }
    }
  };

  const getAssetModelModalActionConfig = (): any => {
    if (isDetails) return [];
    if (isDelete) {
      return [
        {
          label: "Yes",
          event: handleDelete,
          variant: "primary",
        },
      ];
    }
    return [
      {
        label: "Save",
        event: onSubmit,
        variant: "primary",
      },
    ];
  };

  const modalActionConfig = getAssetModelModalActionConfig();

  const actionConfig: any = [
    {
      label: "Edit",
      actionProperty: "assetModelCode",
      event: editAction,
      variant: "secondary",
      className: "mx-2 my-1",
      moduleName: "Asset Model",
      actionName: "Edit-Asset Model",
      icon: faPencilAlt,
    },
    {
      label: "View",
      actionProperty: "assetModelCode",
      event: detailsAction,
      variant: "info",
      className: "mx-2 my-1",
      moduleName: "Asset Model",
      actionName: "View-Asset Model",
      icon: faFileAlt,
    },
    {
      label: "Delete",
      actionProperty: "assetModelCode",
      event: deleteAction,
      variant: "danger",
      className: "mx-2 my-1",
      moduleName: "Asset Model",
      actionName: "Delete-Asset Model",
      icon: faTrashAlt,
    },
  ];

  const emptyConfig = {
    title: "No Asset Model Found",
    description: "",
  };

  async function handleFile(event: React.ChangeEvent<HTMLInputElement>) {
    if (!event.target.files || event.target.files.length === 0) {
      setSelectedFile(null);
      return;
    }

    const isValid = await validateImageFile(event.target.files[0]);
    if (!isValid) {
      event.target.value = "";
      setSelectedFile(null);
      return;
    }

    setSelectedFile(event.target.files[0]);
  }

  useEffect(() => {
    if (!selectedFile) {
      setPreview(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const formConfig: FormConfig = {
    controls: [
      {
        name: "assetModelCode",
        label: "Asset Model",
        placeholder: "eSave-GW-001",
        type: "text",
        required: !isDetails,
        readOnly: isDetails || isEdit,
        disabled: isDetails || isEdit,
      },
      {
        name: "ratedWatt",
        label: "Rated Watt",
        placeholder: "20",
        type: "number",
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
      },
      {
        name: "supplierId",
        label: "Supplier",
        placeholder: "Select Supplier",
        type: "searchable-select",
        options: slsSupplierOptions,
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
      },
      {
        name: "expectedLifeTime",
        label: "Expected Lifetime",
        placeholder: "10000",
        type: "number",
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
      },
      {
        name: "picture",
        label:
          "Picture",
        placeholder: "Choose file",
        type: "file",
        onChange: handleFile,
        filterFileTypes: imageFileTypes,
        // required: true,
        info: "Supported file types: jpg, jpeg, png",
      },
      {
        name: "assetTypeId",
        label: "Asset Type",
        placeholder: "Select Asset Type",
        type: "select",
        options: assetTypeOptions,
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
      },
      {
        name: "description",
        label: "Description",
        placeholder: "Something...",
        type: "textarea",
        required: false,
        readOnly: isDetails,
        disabled: isDetails,
      },
      {
        name: "remark",
        label: "Remarks",
        placeholder: "Something...",
        type: "textarea",
        required: false,
        readOnly: isDetails,
        disabled: isDetails,
      },
    ],
  };

  const validationSchema = yup.object().shape({
    assetModelCode: yup
      .string()
      .required("Asset Model is required")
      .max(50, "Asset Model should not exceed 50 characters"),
    ratedWatt: yup
      .number()
      .min(1, "Minimum value should be greater than 0")
      .required("Rated Watt is required")
      .max(1000, "Rated Watt should not exceed 1000 watts"),
    supplierId: yup.string().required("Supplier is required"),
    expectedLifeTime: yup
      .number()
      .min(1, "Minimum value should be greater than 0")
      .required("Expected Lifetime is required")
      .max(1000000000, "Expected Lifetime should not exceed 1000000000 hours"),
    assetTypeId: yup.string().required("Asset Type is required"),
    // picture: yup.string().required("Picture is required"),
    selectedFile: yup.string().required("Picture is required"),
    description: yup
      .string()
      .max(200, "Description should not exceed 200 characters"),
    remark: yup.string().max(200, "Remarks should not exceed 200 characters"),
  });

  const onRemoveFile = () => {
    setSelectedFile(null);
    setInitialValues({
      ...assetModelDetails,
      picture: null,
    });
    setIsShowImage(false);
  };

  const onClickDownload = (fileType: any) => {
    dispatch(
      downloadAssetModelFile({
        fileType,
        tcCode: null,
        siteCode: null,
        blockCode: null,
        searchText: assetModelSearchValue || "",
        sortBy: null,
        sortDirection: "ASC",
      })
    );
  };

  const getAssetModelModalHeaderTitle = () => {
    if (isEdit) return "Edit Asset Model";
    if (isDetails) return "Asset Model Details";
    if (isDelete) return "Delete Asset Model";
    return "Add Asset Model";
  };

  return (
    <div>
      <div>
        <div className="d-flex gap-3 justify-content-end table-generic-action table-toolbar">
          <div>
            <SearchBarComponent
              placeholder="Search Asset Model"
              methodToDispatch={getAssetModels}
              getSearch={onSearch}
            />
          </div>
          <div className="d-flex gap-3 align-items-center asset-driver-model-button-group">
            <div className="export ">
              <HasPermission
                moduleName="Asset Model"
                actionName="Add-Asset Model"
                isRoute={false}
              >
                <div className="d-flex gap-2">
                  {/* <B2Button
                    className="icon-text-btn ms-2"
                    variant="primary"
                    onClick={() => {
                      onClickDownload("downloadPdf");
                      setIsPdfDownload(true);
                      setIsExcelDownload(false);
                    }}
                  >
                    {isLoadingAssetModelDownload && isPdfDownload ? (
                      <Spinner
                        animation="border"
                        variant="info"
                        size="sm"
                        className="me-2"
                      />
                    ) : (
                      <FontAwesomeIcon icon={faDownload} className="me-2" />
                    )}
                    Export as PDF
                  </B2Button> */}

                  <B2Button
                    className="icon-text-btn"
                    variant="primary"
                    onClick={() => {
                      onClickDownload("downloadExcel");
                      setIsExcelDownload(true);
                      // setIsPdfDownload(false);
                    }}
                  >
                    {isLoadingAssetModelDownload && isExcelDownload ? (
                      <Spinner
                        animation="border"
                        variant="info"
                        size="sm"
                        className="me-2"
                      />
                    ) : (
                      <FontAwesomeIcon icon={faDownload} className="me-2" />
                    )}
                    Export
                  </B2Button>
                </div>
              </HasPermission>
            </div>
            <HasPermission
              moduleName="Asset Model"
              actionName="Add-Asset Model"
              isRoute={false}
            >
              <B2Button
                className="icon-text-btn"
                variant="primary"
                onClick={handleAddAssetModel}
              >
                {/* <FontAwesomeIcon icon={faCirclePlus} className="fs-7 me-2" /> */}
                Add New Asset Model
              </B2Button>
            </HasPermission>
          </div>
        </div>
        <div className="my-3">
          <TableComponent
            tableHeaders={columns} // Action used
            emptyConfig={emptyConfig}
            data={data}
            totalNumberOfRecords={totalNumberOfRecords}
            pageSize={pageSize}
            current={current}
            onChangePage={onChangePage}
            actionConfig={actionConfig}
            onChangeSort={onChangeSort}
            isLoadingData={isLoading}
          />
        </div>
      </div>

      {/* modals */}
      {isModalOpen && (
        <ModalComponent
          isConformationModal={isDelete}
          showModal={isModalOpen}
          handleClose={handleCloseModal}
          size={"lg"}
          headerTitle={getAssetModelModalHeaderTitle()}
          actionConfig={modalActionConfig}
        >
          {isDelete ? (
            <h5 className="text-center">
              Are you sure to <b className="text-danger">Delete</b>?
            </h5>
          ) : (
            <SkeletonComponent
              wrapperHeight="480px"
              skeletonHeight="50px"
              isLoading={isLoadingAssetModelDetails || isLoadingSls}
              count={8}
            >
              <FormBuilderComponent
                formRef={formRef}
                initialValues={initialValues}
                validation={validationSchema}
                formConfig={formConfig}
                fileConfig={{
                  isShowImage,
                  isEdit,
                  onRemoveFile: onRemoveFile,
                  preview,
                }}
              />
            </SkeletonComponent>
          )}
        </ModalComponent>
      )}
    </div>
  );
};

export default AssetModelTableComponent;
